import JSZip from 'jszip'
import { saveAs } from 'file-saver'

/**
 * Downloads multiple files and creates a zip archive
 * @param {Array} items - Array of items to download
 * @param {Object} options - Configuration options
 * @param {Function} options.filterFn - Function to filter downloadable items
 * @param {Function} options.getUrlFn - Function to get download URL from item
 * @param {Function} options.getFilenameFn - Function to get filename for item
 * @param {String} options.zipFilename - Name for the zip file (without extension)
 * @param {Object} options.compressionOptions - JSZip compression options
 * @returns {Promise<Object>} Result object with success status and details
 */
export async function downloadAsZip (items, options = {}) {
  const {
    filterFn = (item) => true,
    getUrlFn = (item) => item.downloadUrl,
    getFilenameFn = (item) => item.name,
    zipFilename = 'download',
    compressionOptions = {
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    }
  } = options

  // Filter items
  const downloadableItems = items.filter(filterFn)

  if (downloadableItems.length === 0) {
    return {
      success: false,
      message: 'Geen items beschikbaar om te downloaden',
      downloadedCount: 0,
      failedCount: 0
    }
  }

  // Create JSZip instance
  const zip = new JSZip()
  const failedDownloads = []
  const successfulDownloads = []

  // Download all files
  const downloadPromises = downloadableItems.map(async (item) => {
    try {
      const url = getUrlFn(item)
      if (!url) {
        throw new Error('Geen download URL beschikbaar')
      }

      // Fetch the file
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Get the blob
      const blob = await response.blob()

      // Get filename
      const filename = getFilenameFn(item)

      // Add to zip
      zip.file(filename, blob)

      successfulDownloads.push({
        item,
        filename
      })

      return { success: true, item }
    } catch (error) {
      console.error('Failed to download item:', error, item)
      failedDownloads.push({
        item,
        error: error.message
      })
      return { success: false, item, error }
    }
  })

  // Wait for all downloads
  await Promise.all(downloadPromises)

  // Check if we have any successful downloads
  if (successfulDownloads.length === 0) {
    return {
      success: false,
      message: 'Alle downloads zijn mislukt',
      downloadedCount: 0,
      failedCount: failedDownloads.length,
      failedItems: failedDownloads
    }
  }

  try {
    // Generate zip file
    const zipBlob = await zip.generateAsync(compressionOptions)

    // Add timestamp to filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19)
    const finalZipFilename = `${zipFilename}_${timestamp}.zip`

    // Trigger download
    saveAs(zipBlob, finalZipFilename)

    return {
      success: true,
      message: successfulDownloads.length === downloadableItems.length
        ? `Succesvol ${successfulDownloads.length} item${successfulDownloads.length > 1 ? 's' : ''} gedownload`
        : `${successfulDownloads.length} van ${downloadableItems.length} items gedownload`,
      downloadedCount: successfulDownloads.length,
      failedCount: failedDownloads.length,
      failedItems: failedDownloads,
      zipFilename: finalZipFilename
    }
  } catch (error) {
    console.error('Error creating zip file:', error)
    return {
      success: false,
      message: `Fout bij maken van zip bestand: ${error.message}`,
      downloadedCount: successfulDownloads.length,
      failedCount: failedDownloads.length,
      error
    }
  }
}

/**
 * Get file extension based on format
 * @param {String} format - Format string (Word/Excel)
 * @returns {String} File extension
 */
function getFileExtension (format) {
  if (!format) return '.docx'
  const formatLower = format.toLowerCase()
  if (formatLower === 'excel') return '.xlsx'
  if (formatLower === 'word') return '.docx'
  return '.docx'
}

/**
 * Get filename with correct extension
 * @param {Object} item - Export item
 * @returns {String} Filename with extension
 */
function getFilenameWithExtension (item) {
  const extension = getFileExtension(item.format)
  const basename = item.exportNaam || 'export'
  // Check if filename already has the correct extension
  if (basename.endsWith(extension)) {
    return basename
  }
  // Remove any existing extension and add the correct one
  const nameWithoutExt = basename.replace(/\.[^/.]+$/, '')
  return `${nameWithoutExt}${extension}`
}

/**
 * Specialized function for downloading export groups
 * @param {Array} exports - Array of export items
 * @param {String} groupName - Name of the export group
 * @returns {Promise<Object>} Result object with success status and details
 */
export async function downloadExportGroupAsZip (exports, groupName = 'exports') {
  return downloadAsZip(exports, {
    filterFn: (item) => item.downloadUrl && item.exportStatusText === 'Export klaar',
    getUrlFn: (item) => item.downloadUrl,
    getFilenameFn: (item) => {
      return getFilenameWithExtension(item)
    },
    zipFilename: groupName
  })
}
